import React, { useEffect, useState, useCallback } from 'react'
import { Box, Button, Center, Loader, Text } from '@mantine/core'
import { FiArrowLeft } from 'react-icons/fi'
import { useHistory, useParams } from 'react-router-dom'
import agent from '../../app/api/agent'
import type { ShootDto as ApiShootDto } from '../../app/models/shoot'
import ShootCard from '../../components/FeedCards/ShootCard'
import type { ShootDto as CardShootDto } from '../../components/FeedCards/ShootCard'
import { toAbsoluteMediaUrl } from '../../app/api/resolveApiBaseUrl'

function apiShootToCard (d: ApiShootDto): CardShootDto {
  const linked = [d.firearmModelName, d.cartridgeLabel].filter(Boolean).join(' · ')
  return {
    id: String(d.id),
    userId: d.userId,
    mediaUrl: toAbsoluteMediaUrl(d.mediaUrl),
    mediaType: d.mediaType === 'video' ? 'video' : 'image',
    textBoxesJson: d.textBoxesJson ?? '',
    createdAt: d.createdAt,
    expiresAt: d.expiresAt ?? new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
    userAvatar: d.userAvatarUrl ?? '',
    userName: (d.userDisplayName || d.username || 'Member').trim(),
    likeCount: d.likeCount ?? 0,
    commentCount: d.commentCount ?? 0,
    linkedCaption: linked || undefined
  }
}

export default function ShootDetailPage () {
  const history = useHistory()
  const { shootId } = useParams<{ shootId: string }>()
  const [shoot, setShoot] = useState<CardShootDto | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  const load = useCallback(async () => {
    if (!shootId) return
    setLoading(true)
    setError(null)
    try {
      const d = await agent.Shoots.details(shootId)
      setShoot(apiShootToCard(d))
    } catch {
      setError('This shoot is not available or has expired.')
      setShoot(null)
    } finally {
      setLoading(false)
    }
  }, [shootId])

  useEffect(() => {
    void load()
  }, [load])

  return (
    <Box sx={{ maxWidth: 520, margin: '0 auto', padding: '16px' }}>
      <Button
        variant="subtle"
        leftIcon={<FiArrowLeft size={14} />}
        onClick={() => history.push('/slop-feed')}
        sx={{ color: 'var(--gs-muted)', marginBottom: 16, paddingLeft: 0 }}
      >
        Slop feed
      </Button>

      {loading && (
        <Center py="xl">
          <Loader size="md" />
        </Center>
      )}

      {!loading && error && (
        <Text size="sm" c="dimmed" ta="center">
          {error}
        </Text>
      )}

      {!loading && shoot && <ShootCard shoot={shoot} hideToolbar />}
    </Box>
  )
}
